/* iter-p2-m — /my-library page.
 *
 * A logged-in buyer's re-download grid.  Reads GET /downloads/my-library
 * and renders a cover + title card per owned book with a "Open reader"
 * link that jumps to /read/<token>.  Buyers never lose access.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, ArrowRight } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function MyLibraryPage() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/downloads/my-library`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || 'Please sign in to see your library.'));
  }, []);

  if (items === null && !err) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (err) {
    return (
      <div className="mx-auto max-w-md p-8 text-center" data-testid="my-library-locked">
        <div className="text-[16px] font-semibold">My Library</div>
        <p className="mt-1 text-[13px] text-muted-foreground">{err}</p>
        <Link to="/bookstore/login" className="mt-3 inline-block text-[12px] text-primary underline">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="my-library-page">
      <div className="mb-1 text-[10.5px] uppercase tracking-wider text-primary">Your books</div>
      <h1 className="text-[26px] leading-tight"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
        My Library
      </h1>
      <p className="mt-1 text-[12.5px] text-muted-foreground">
        Every book you've purchased on Crown &amp; Rose. Re-download anytime — access never expires.
      </p>

      {items.length === 0 ? (
        <div className="mt-8 rounded-md border border-dashed border-border p-8 text-center"
             data-testid="my-library-empty">
          <BookOpen className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-2 text-[13px] font-semibold">No books yet</div>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Buy a book and it'll land here, ready to send to any of your reading devices.
          </p>
          <Link to="/bookstore" className="mt-3 inline-block text-[12px] text-primary underline"
                data-testid="my-library-browse">
            Browse the store →
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-library-grid">
          {items.map((it) => (
            <Link key={it.token} to={`/read/${it.token}`}
                  className="group flex gap-3 rounded-md border border-border bg-card p-3 transition hover:border-primary"
                  data-testid={`my-library-card-${it.book_id}`}>
              {it.cover_url ? (
                <img src={it.cover_url} alt="Cover"
                     className="h-24 w-16 flex-none rounded-sm border border-border object-cover" />
              ) : (
                <div className="flex h-24 w-16 flex-none items-center justify-center rounded-sm border border-border bg-panel-header/40">
                  <BookOpen className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{it.title}</div>
                <div className="truncate text-[11.5px] text-muted-foreground">{it.author || ''}</div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                  Downloaded {it.download_count || 0}×
                </div>
                <div className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-primary group-hover:underline">
                  Open reader <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
